import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useArtigos, useCategorias, formatarData } from "@/hooks/useBlog";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const fadeUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Blog() {
  const [categoriaAtiva, setCategoriaAtiva] = useState<string | undefined>();
  const [paginaAtual, setPaginaAtual] = useState(1);

  const { data: categorias, isLoading: loadingCategorias } = useCategorias();
  const { data: artigosData, isLoading: loadingArtigos } = useArtigos(
    categoriaAtiva,
    paginaAtual,
    12
  );

  const handleCategoriaClick = (slug: string | undefined) => {
    setCategoriaAtiva(slug);
    setPaginaAtual(1);
  };

  return (
    <>
      <Helmet>
        <title>Blog | Aceena - Carro por Assinatura com Curadoria</title>
        <meta
          name="description"
          content="Artigos, dicas e novidades sobre carro por assinatura. Análises de modelos, comparativos, guias práticos e tendências do mercado automotivo brasileiro."
        />
        <meta name="keywords" content="carro por assinatura, blog automotivo, dicas carro assinatura, comparativo carros, modelos assinatura" />
        <link rel="canonical" href="https://www.aceena.com.br/blog" />
        <meta property="og:title" content="Blog | Aceena - Carro por Assinatura com Curadoria" />
        <meta property="og:description" content="Artigos, dicas e novidades sobre carro por assinatura." />
        <meta property="og:url" content="https://www.aceena.com.br/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section - Clean white design */}
        <section className="pt-24 lg:pt-32 pb-12 lg:pb-16 bg-background border-b border-border">
          <div className="container">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              className="text-center max-w-3xl mx-auto"
            >
              <span className="inline-block text-sm font-medium text-aceena-green mb-4 tracking-wide uppercase">
                Conteúdo Especializado
              </span>
              <h1 className="text-3xl lg:text-5xl font-bold text-primary mb-4">
                Blog Aceena
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Conteúdo especializado sobre carro por assinatura, análises de
                modelos, dicas práticas e tendências do mercado automotivo.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categorias Filter */}
        <section className="py-6 border-b border-border sticky top-16 lg:top-20 bg-background z-40">
          <div className="container">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => handleCategoriaClick(undefined)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                  !categoriaAtiva
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                )}
              >
                Todos
              </button>
              {loadingCategorias ? (
                <>
                  <Skeleton className="h-9 w-32 rounded-full" />
                  <Skeleton className="h-9 w-28 rounded-full" />
                  <Skeleton className="h-9 w-36 rounded-full" />
                </>
              ) : (
                categorias?.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoriaClick(cat.slug)}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                      categoriaAtiva === cat.slug
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {cat.nome}
                  </button>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 lg:py-16">
          <div className="container">
            {loadingArtigos ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-4">
                    <Skeleton className="aspect-[16/10] w-full rounded-xl" />
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
            ) : artigosData?.artigos.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">
                  Nenhum artigo encontrado nesta categoria.
                </p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {artigosData?.artigos.map((artigo, index) => (
                    <motion.article
                      key={artigo.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className={cn(
                        "group bg-card rounded-xl overflow-hidden border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300",
                        artigo.destaque && "ring-2 ring-aceena-green/50"
                      )}
                    >
                      <Link to={`/blog/${artigo.slug}`} className="block">
                        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                          {artigo.imagem_destaque_url ? (
                            <img
                              src={artigo.imagem_destaque_url}
                              alt={artigo.titulo}
                              loading="lazy"
                              className="w-full h-full object-contain bg-muted group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/5 to-aceena-green/5">
                              <span className="text-4xl font-bold text-primary/20">
                                {artigo.titulo.charAt(0)}
                              </span>
                            </div>
                          )}
                          {artigo.destaque && (
                            <span className="absolute top-3 left-3 bg-aceena-green text-primary px-3 py-1 rounded-full text-xs font-semibold">
                              Destaque
                            </span>
                          )}
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-3 mb-3">
                            {artigo.categoria && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-aceena-green">
                                <Tag className="w-3 h-3" />
                                {artigo.categoria.nome}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              {formatarData(artigo.data_publicacao)}
                            </span>
                          </div>
                          <h2 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                            {artigo.titulo}
                          </h2>
                          {artigo.resumo && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {artigo.resumo}
                            </p>
                          )}
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>

                {/* Pagination */}
                {artigosData && artigosData.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                      disabled={paginaAtual === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(artigosData.totalPages)].map((_, i) => (
                        <Button
                          key={i}
                          variant={paginaAtual === i + 1 ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setPaginaAtual(i + 1)}
                          className="w-9 h-9"
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        setPaginaAtual((p) =>
                          Math.min(artigosData.totalPages, p + 1)
                        )
                      }
                      disabled={paginaAtual === artigosData.totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
